import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function PublicFeedback() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", branch: "",
    type: "compliment", subject: "", message: "", rating: 5,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const b = searchParams.get("branch");
    if (b) setForm((f) => ({ ...f, branch: b }));
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/feedback", { tenant_slug: "abrah", ...form, rating: Number(form.rating) || null });
      setDone(true);
      toast.success("Thanks — we've received your feedback");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <div className="font-display text-3xl font-bold text-emerald-900">Thank you!</div>
        <p className="text-gray-600 mt-2">Your feedback has been received. Our customer care team reviews every submission within 48 hours.</p>
        <Button onClick={() => { setDone(false); setForm({ ...form, subject: "", message: "" }); }}
          className="mt-6 bg-emerald-900 hover:bg-emerald-950 text-white">
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-bold text-gray-900 mb-1">Tell us anything</h1>
      <p className="text-sm text-gray-600 mb-6">No account needed — share a compliment, complaint, suggestion or question. We read every message.</p>
      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-md p-5 space-y-4" data-testid="public-feedback-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Your name (optional)" value={form.customer_name}
            onChange={(v) => setForm({ ...form, customer_name: v })} />
          <Field label="Phone (optional)" value={form.customer_phone}
            onChange={(v) => setForm({ ...form, customer_phone: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email (optional)" value={form.customer_email}
            onChange={(v) => setForm({ ...form, customer_email: v })} />
          <div>
            <Label>Branch (if applicable)</Label>
            <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })}>
              <option value="">Any / general</option>
              {["Main Abrah", "New Abrah", "Old Abrah", "Abrah Jinja", "Abrah Arua Park", "Abrah Kikubo"].map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label>Type of feedback</Label>
          <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {["compliment", "complaint", "suggestion", "question"].map((t) => (
              <button type="button" key={t} onClick={() => setForm({ ...form, type: t })}
                data-testid={`feedback-type-${t}`}
                className={`px-3 py-2 rounded-md text-sm border ${
                  form.type === t ? "bg-emerald-900 text-white border-emerald-900" :
                  "bg-white text-gray-700 border-gray-300 hover:border-emerald-700"
                }`}>{t}</button>
            ))}
          </div>
        </div>
        <Field label="Subject" required value={form.subject}
          onChange={(v) => setForm({ ...form, subject: v })} testid="feedback-subject-input" />
        <div>
          <Label>Message</Label>
          <Textarea rows={5} required value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1"
            data-testid="feedback-message-input" />
        </div>
        <div>
          <Label>Rating (1-5)</Label>
          <div className="mt-1 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}
                className={`h-9 w-9 rounded-md text-lg ${
                  n <= Number(form.rating) ? "bg-amber-400 text-amber-950" : "bg-gray-100 text-gray-400"
                }`}>★</button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={busy}
          className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
          data-testid="feedback-submit-button">
          {busy ? "Submitting…" : "Send feedback"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
      <Input required={required} value={value}
        onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}
